/**
 * Reusable Filter Builder Component
 */

import * as React from 'react';
import { 
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input
} from '@/components/ui';
import { toast } from '@/components/ui';
import { 
  Plus,
  X,
} from 'lucide-react';
import { FilterCondition, STRING_OPERATIONS, NUMBER_OPERATIONS, DATE_OPERATIONS } from './types';

export interface FilterBuilderProps {
  /**
   * Available columns to filter on
   */
  columns: any[];
  
  /**
   * Current WHERE clause
   */
  whereClause: string;
  
  /**
   * Function to update the WHERE clause
   */
  onWhereChange: (where: string) => void;
  
  /**
   * Whether to show the advanced mode toggle
   * @default true
   */
  showAdvancedToggle?: boolean;
  
  /**
   * Whether to show the advanced filter mode
   * If provided, this will override the internal state
   */
  showAdvancedFilter?: boolean;
  
  /**
   * Function to call when advanced filter mode changes
   * Used to coordinate multiple filter builders
   */
  onAdvancedFilterChange?: (isAdvanced: boolean) => void;
  
  /**
   * Placeholder text for the advanced mode input
   * @default "e.g. age > 30 AND country = 'USA'"
   */
  advancedPlaceholder?: string;
  
  /**
   * Help text for the advanced mode
   * @default "Use SQL WHERE syntax for advanced filtering"
   */
  advancedHelpText?: string;
}

/**
 * Reusable Filter Builder Component
 */
export function FilterBuilder({ 
  columns, 
  whereClause, 
  onWhereChange,
  showAdvancedToggle = true,
  showAdvancedFilter: externalAdvancedFilter,
  onAdvancedFilterChange,
  advancedPlaceholder = "e.g. age > 30 AND country = 'USA'",
  advancedHelpText = "Use SQL WHERE syntax for advanced filtering"
}: FilterBuilderProps) {
  // State for filter conditions
  const [filterConditions, setFilterConditions] = React.useState<FilterCondition[]>([]);
  const [internalAdvancedFilter, setInternalAdvancedFilter] = React.useState(false);
  
  // Use external advanced filter state if provided, otherwise use internal state
  const showAdvancedFilter = externalAdvancedFilter !== undefined 
    ? externalAdvancedFilter 
    : internalAdvancedFilter;
  
  // Check if any columns used in filter conditions are no longer available
  React.useEffect(() => {
    if (filterConditions.length > 0) {
      const availableColumnNames = columns.map(c => c.name);
      const hasInvalidColumns = filterConditions.some(
        condition => !availableColumnNames.includes(condition.column)
      );
      
      // Check if any operations are invalid for the column type
      const hasInvalidOperations = filterConditions.some(condition => {
        const columnObj = columns.find(c => c.name === condition.column);
        if (!columnObj) return false;
        
        const validOperations = getOperationsForColumn(columnObj).map(op => op.value);
        return !validOperations.includes(condition.operation);
      });
      
      if (hasInvalidColumns || hasInvalidOperations) {
        // Remove filter conditions with invalid columns or operations
        const validConditions = filterConditions.filter(condition => {
          // Check if column exists
          if (!availableColumnNames.includes(condition.column)) return false;
          
          // Check if operation is valid for column type
          const columnObj = columns.find(c => c.name === condition.column);
          if (!columnObj) return false;
          
          const validOperations = getOperationsForColumn(columnObj).map(op => op.value);
          return validOperations.includes(condition.operation);
        });
        
        setFilterConditions(validConditions);
        
        // Update the WHERE clause
        const whereClause = buildWhereClause(validConditions);
        onWhereChange(whereClause);
        
        // Show a notification
        if (validConditions.length !== filterConditions.length) {
          toast.info('Some filter conditions were removed because they referenced columns or operations that are no longer valid', {
            duration: 5000,
            closeButton: true,
          });
        }
      }
    }
  }, [columns, filterConditions, onWhereChange]);
  
  // Initialize filter conditions from WHERE clause on mount
  React.useEffect(() => {
    if (whereClause) {
      // This is a simplified approach - in a real app, you'd parse the WHERE clause
      // into structured filter conditions using a SQL parser library
      // For example, you could use 'node-sql-parser' to convert the SQL WHERE clause
      // into an AST and then transform it into filter conditions
      if (externalAdvancedFilter === undefined) {
        setInternalAdvancedFilter(true);
        if (onAdvancedFilterChange) {
          onAdvancedFilterChange(true);
        }
      }
    }
  }, [whereClause, externalAdvancedFilter, onAdvancedFilterChange]);
  
  // Function to toggle advanced filter mode
  const toggleAdvancedFilter = () => {
    const newValue = !showAdvancedFilter;
    
    // Update internal state if we're not controlled externally
    if (externalAdvancedFilter === undefined) {
      setInternalAdvancedFilter(newValue);
    }
    
    // Notify parent component if callback is provided
    if (onAdvancedFilterChange) {
      onAdvancedFilterChange(newValue);
    }
  };
  
  // Get the operations available for a column based on its type
  const getOperationsForColumn = (column: any) => {
    const type = column.schema.type.toString().toLowerCase();
    
    if (type.includes('int') || type.includes('float') || type.includes('double') || type.includes('decimal') || type.includes('number')) {
      return NUMBER_OPERATIONS;
    } else if (type.includes('date') || type.includes('time')) {
      return DATE_OPERATIONS;
    } else {
      return STRING_OPERATIONS;
    }
  };
  
  // Function to build a WHERE clause from filter conditions
  const buildWhereClause = (conditions: FilterCondition[]): string => {
    try {
      if (conditions.length === 0) return '';
      
      return conditions.map((condition, index) => {
        const { column, operation, value, conjunction } = condition;
        
        // Find the column object
        const columnObj = columns.find(c => c.name === column);
        if (!columnObj) return '';
        
        // Find the operation object
        const operations = getOperationsForColumn(columnObj);
        const operationObj = operations.find(op => op.value === operation);
        if (!operationObj) return '';
        
        // Build the clause
        const clause = operationObj.sqlTemplate(column, value);
        
        // Add conjunction if not the first condition
        return index === 0 ? clause : `${conjunction} ${clause}`;
      }).join(' ');
    } catch (error) {
      console.error('Error building WHERE clause:', error);
      return '';
    }
  };
  
  // Function to add a new filter condition
  const addFilterCondition = () => {
    try {
      if (columns.length === 0) return;
      
      // Make sure we have a valid column
      const firstColumn = columns[0];
      if (!firstColumn) return;
      
      const newCondition: FilterCondition = {
        column: firstColumn.name,
        operation: 'equals',
        value: '',
        conjunction: 'AND'
      };
      
      const newConditions = [...filterConditions, newCondition];
      setFilterConditions(newConditions);
      
      // Update the WHERE clause
      const whereClause = buildWhereClause(newConditions);
      onWhereChange(whereClause);
    } catch (error) {
      // Show error in toast notification
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'string' 
          ? error 
          : 'An error occurred while adding filter condition';
      
      toast.error(errorMessage, {
        description: 'Please try again.',
        duration: 5000,
        closeButton: true,
      });
      
      console.error('Error adding filter condition:', error);
    }
  };
  
  // Function to update a filter condition
  const updateFilterCondition = (index: number, updates: Partial<FilterCondition>) => {
    try {
      // Make sure we have valid conditions
      if (index < 0 || index >= filterConditions.length) {
        return;
      }
      
      // Create a new array with the updated condition
      const newConditions = filterConditions.map((condition, i) => {
        if (i !== index) return condition;
        
        // Return a new condition with the updates
        return {
          column: updates.column !== undefined ? updates.column : condition.column,
          operation: updates.operation !== undefined ? updates.operation : condition.operation,
          value: updates.value !== undefined ? updates.value : condition.value,
          conjunction: updates.conjunction !== undefined ? updates.conjunction : condition.conjunction
        };
      });
      
      setFilterConditions(newConditions);
      
      // Update the WHERE clause
      const whereClause = buildWhereClause(newConditions);
      onWhereChange(whereClause);
    } catch (error) {
      // Show error in toast notification
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'string' 
          ? error 
          : 'An error occurred while updating filter condition';
      
      toast.error(errorMessage, {
        description: 'Please check your filter settings and try again.',
        duration: 5000,
        closeButton: true,
      });
      
      console.error('Error updating filter condition:', error);
    }
  };
  
  // Function to remove a filter condition
  const removeFilterCondition = (index: number) => {
    try {
      const newConditions = filterConditions.filter((_, i) => i !== index);
      setFilterConditions(newConditions);
      
      // Update the WHERE clause
      const whereClause = buildWhereClause(newConditions);
      onWhereChange(whereClause);
    } catch (error) {
      // Show error in toast notification
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'string' 
          ? error 
          : 'An error occurred while removing filter condition';
      
      toast.error(errorMessage, {
        description: 'Please try again.',
        duration: 5000,
        closeButton: true,
      });
      
      console.error('Error removing filter condition:', error);
    }
  };
  
  return (
    <div className="space-y-2">
      {!showAdvancedFilter && (
        <div className="space-y-2">
          {filterConditions.map((condition, index) => {
            // Find the column object
            const columnObj = columns.find(c => c.name === condition.column);
            const operations = columnObj ? getOperationsForColumn(columnObj) : [];
            
            return (
              <div key={index} className="flex items-center gap-2">
                {index > 0 && (
                  <Select
                    value={condition.conjunction}
                    onValueChange={(value) => updateFilterCondition(index, { conjunction: value as 'AND' | 'OR' })}
                  >
                    <SelectTrigger className="w-[70px] h-8 text-xs">
                      <SelectValue placeholder="AND" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AND">AND</SelectItem>
                      <SelectItem value="OR">OR</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                
                <Select
                  value={condition.column}
                  onValueChange={(value) => updateFilterCondition(index, { column: value })}
                >
                  <SelectTrigger className="w-[120px] h-8 text-xs">
                    <SelectValue placeholder="Column" />
                  </SelectTrigger>
                  <SelectContent>
                    {columns.map((column) => (
                      <SelectItem key={column.name} value={column.name}>
                        {column.name}
                        {column._sourceAggregate && (
                          <span className="ml-1 text-muted-foreground">
                            ({column._sourceAggregate.operation}({column._sourceAggregate.column}))
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select
                  value={condition.operation}
                  onValueChange={(value) => updateFilterCondition(index, { operation: value })}
                >
                  <SelectTrigger className="w-[120px] h-8 text-xs">
                    <SelectValue placeholder="Operation" />
                  </SelectTrigger>
                  <SelectContent>
                    {operations.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Input
                  value={condition.value}
                  onChange={(e) => updateFilterCondition(index, { value: e.target.value })}
                  placeholder="Value"
                  className="flex-1 h-8 text-xs"
                />
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-destructive"
                  onClick={() => removeFilterCondition(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
          
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={addFilterCondition}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Filter
          </Button>
        </div>
      )}
      
      {showAdvancedFilter && (
        <div className="space-y-2">
          <Input
            value={whereClause || ''}
            onChange={(e) => onWhereChange(e.target.value)}
            placeholder={advancedPlaceholder}
            className="text-xs"
          />
          <div className="text-xs text-muted-foreground">
            {advancedHelpText}
          </div>
        </div>
      )}
    </div>
  );
} 